<script setup>
import { ref, onMounted, watch, computed, onBeforeUnmount } from 'vue';
import AppLoader from '../../components/AppLoader.vue';
import AppHeader from '../../components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import { useSourcesStore } from '../../stores/sources.store';
import { useDestinationsStore } from '../../stores/destinations.store';
import { usePipelinesStore } from '../../stores/pipelines.store';
import { useTransformsStore } from '../../stores/transforms.store';
import { useMetricsStore } from '@/stores/metrics.store';
import { useTagsStore } from '@/stores/tags.store';
import { useAuthStore } from '@/stores/auth.store';
import ErrorMessage from '../../components/ErrorMessage.vue';
import TreeView from '../../components/TreeView.vue';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import _ from 'lodash';
import RowActionMenu from '@/components/RowActionMenu.vue';
import StatCard from '@/components/global/StatCard.vue';
import { toast } from 'vue3-toastify';
import { userHasAccess, getGlobalConfigValue } from '../../utils/utils';
import { TAG_BADGE_COLOR_CLASSES_PER_TYPE, TAG_TYPES } from '@/utils/constants';
import PeriodicAuditConfig from '@/components/pipelines/PeriodicAuditConfig.vue';

import {
    InformationCircleIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    CpuChipIcon,
    AdjustmentsVerticalIcon,
    QuestionMarkCircleIcon,
    MagnifyingGlassIcon,
    ClockIcon
} from '@heroicons/vue/24/outline';
import { QueueListIcon } from '@heroicons/vue/24/solid';

import { buildTreeNodesOutOfExistingConfig, getStatusColor, formatter, compareWithOmittedProperties } from '../../utils/utils';
import { getConnectorIcon, getConnectorDisplayName, MAX_ONE_TAG_PER_TYPE } from '@/utils/constants';
import Tabs from '@/components/global/Tabs.vue';
import useErrorDialog from '../../composables/useErrorDialog.js';
import useConfirmBeforeAction from '@/composables/useConfirmBeforeAction';
import Multiselect from 'vue-multiselect';
import WebSocketService from '../../services/webSocket.service';
import YesNoConfirmModal from '@/components/global/modals/YesNoConfirmModal.vue';

const props = defineProps(['id', 'customMessage', 'source', 'destination']);
const pipelinesStore = usePipelinesStore();
const sourcesStore = useSourcesStore();
const destinationsStore = useDestinationsStore();
const tagsStore = useTagsStore();
const transformsStore = useTransformsStore();
const metricsStore = useMetricsStore();
const authStore = useAuthStore();

let pipeline = ref(null);
let pipelineSource = ref(null);
let pipelineSourceConfig = ref(null);
let router = useRouter();
let route = useRoute();

let generalLoading = ref(false);
let actionLoading = ref(false);
let configurationSchemaLoading = ref(false);
let tagsLoading = ref(false);
let tags = ref([]);
let transformsLoading = ref(false);

let defaultTransformsTree = ref([]);
let defaultSchemaTree = ref([]);
let hidePromptTemporary = ref(false);
let isTopicsConfirmModalOpen = ref(false);

let formError = ref('');
let treeLevels = ref([]);
let treeNodes = ref([]);
let transformTreeNodes = ref([]);
let transformTreeLevels = ref([]);
let metricsLoading = ref(false);
let snapshotNewTables = ref(true);
let periodicAuditConfig = ref({ enabled: false, topics: [], timestampColumn: '', intervalMinutes: null });
let defaultPipelineName = ref('');
let defaultPipelineTags = ref([]);

let auditValid = ref(true);

watch(
    periodicAuditConfig,
    () => {
        // re-trigger tree-validity logic when audit config changes
        auditValid.value =
            !periodicAuditConfig.value.enabled ||
            (periodicAuditConfig.value.topics.length > 0 &&
                periodicAuditConfig.value.timestampColumn &&
                periodicAuditConfig.value.intervalMinutes > 0);
    },
    { deep: true, immediate: true }
);

/** detect if audit config diverged from default */
let defaultAuditConfig = ref(null);

let selectedTags = ref([]);

let canSaveTree = ref(true);
let areTreesEqual = ref(false);

const tabs = computed(() => [
    { name: 'Status', href: '#', current: true },
    { name: 'Settings', href: '#', current: false },
    {
        name: 'Source',
        href: `/connectors/sources/${route.query.source || pipeline.value?.source?.id || ''}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        newTabUrl: true,
        current: false
    },
    {
        name: 'Destination',
        href: `/connectors/destinations/${route.query.destination || pipeline.value?.destination?.id || ''}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        newTabUrl: true,
        current: false
    }
]);

const showStopSource = computed(() => {
    return (
        pipeline.value?.source.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.source.connector_status?.toLowerCase() === 'paused' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'starting' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'stopped'
        )
    );
});

const validationClass = computed(() => {
    if (!tagsLoading.value && !actionLoading.value && !selectedTags.value.length) {
        return 'border! border-red-500!'; // Tailwind's red border class
    }

    return 'border! border-gray-300!'; // Default border class
});

const showResumeSource = computed(() => {
    return (
        pipeline.value?.source.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.source.connector_status?.toLowerCase() === 'active' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'broken' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'starting'
        )
    );
});

const showStopDestination = computed(() => {
    return (
        pipeline.value?.destination.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.destination.connector_status?.toLowerCase() === 'paused' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'starting' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'stopped'
        )
    );
});

const showResumeDestination = computed(() => {
    return (
        pipeline.value?.destination.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.destination.connector_status?.toLowerCase() === 'active' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'broken' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'starting'
        )
    );
});

/** Audit feature available only for mysql->snowflake or postgresql->clickhouse pipelines */
const isAuditAllowed = computed(() => {
    return pipeline.value.allow_audit;
});

// const onPauseDestination = async () => {
//     try {
//         actionLoading.value = true;
//         await destinationsStore.pause(pipeline.value.destination.id, true);
//         actionLoading.value = false;
//         toast.success('Destination paused successfully.');

//         metricsLoading.value = true;
//         const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
//         pipeline.value = promises[1];
//         metricsLoading.value = false;
//     } catch (e) {
//         toast.error('There was an error while pausing the destination. Please try again.');
//     } finally {
//         actionLoading.value = false;
//         metricsLoading.value = false;
//     }
// };

const onStopDestination = async () => {
    try {
        actionLoading.value = true;
        await destinationsStore.stop(pipeline.value.destination.id, true);
        actionLoading.value = false;
        toast.success('Destination stopped successfully.');

        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];
        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while stopping the destination. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

const onResumeDestination = async () => {
    try {
        actionLoading.value = true;
        await destinationsStore.resume(pipeline.value.destination.id, true);
        actionLoading.value = false;
        toast.success('Destination resumed successfully.');

        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];
        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while resuming the destination. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

const onStopSource = async () => {
    try {
        actionLoading.value = true;
        await sourcesStore.stop(pipeline.value.source.id, true);
        actionLoading.value = false;
        toast.success('Source stopped successfully.');

        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];

        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while stopping the source. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

// const onPauseSource = async () => {
//     try {
//         actionLoading.value = true;
//         await sourcesStore.pause(pipeline.value.source.id, true);
//         actionLoading.value = false;
//         toast.success('Source paused successfully.');

//         metricsLoading.value = true;
//         const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
//         pipeline.value = promises[1];

//         metricsLoading.value = false;
//     } catch (e) {
//         toast.error('There was an error while pausing the source. Please try again.');
//     } finally {
//         actionLoading.value = false;
//         metricsLoading.value = false;
//     }
// };

const onResumeSource = async () => {
    try {
        actionLoading.value = true;
        await sourcesStore.resume(pipeline.value.source.id, true);
        actionLoading.value = false;

        toast.success('Source resumed successfully.');
        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];

        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while resuming the source. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

const pipelineLatestMetrics = computed(() => {
    return metricsStore.pipelines.allLatest[pipeline.value?.id];
});

const stats = computed(() => {
    return [
        {
            id: 0,
            name: 'Status',
            stat: pipelineLatestMetrics.value?.inline?.status ?? '-',
            icon: CheckCircleIcon,
            colorClass: getStatusColor(pipelineLatestMetrics.value?.inline?.status, false, true)
        },
        {
            id: 2,
            name: 'Topics',
            stat: pipeline.value.topics.length,
            icon: QueueListIcon,
            colorClass: 'bg-green-400'
        },
        {
            id: 4,
            name: 'Latency',
            icon: ClockIcon,
            colorClass: 'bg-yellow-500',
            stat: `<div class="flex flex-row items-center gap-3" title="High Latency">
                ${getFormattedValue('latency', 'milliseconds').display}
                                    ${
    pipelineLatestMetrics.value?.inline?.latency >= 60000
        ? `<span class="shadow-xs">
                                            <div
                                                class="h-4 w-4 shrink-0 text-[#236C9E]"
                                                style="display: block; margin: auto"
                                                >
                                                <svg data-v-3f029013="" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"  class="h-5 w-5 text-gray-600 ml-2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                </div>
                                        </span>`
        : ``
}
                                   </div>     `
        },
        {
            id: 4,
            name: 'Lag',
            stat: getFormattedValue('recordsLag', 'count').display,
            icon: CpuChipIcon,
            colorClass: 'bg-purple-400'
        }
    ];
});

const formattedTopicsAndTransforms = computed(() => {
    const transforms = _.map(pipeline.value.transforms, (transform) => {
        if (transform.topic_id) {
            const split = transform.topic_id.split('.');
            const head = split.shift();

            return {
                id: split.length ? head : '-',
                name: split.length ? split.join('.') : head,
                topic_id: transform.topic_id,
                transform_name: transform.name,
                transform: true
            };
        } else {
            return {
                id: transform.id,
                name: transform.name,
                transform: true
            };
        }
    });

    const transformIds = _.map(
        transforms.filter((t) => t.topic_id),
        (transform) => transform.topic_id
    );

    const topics = _.map(
        pipeline.value.topic_ids.filter((t) => !transformIds.includes(t)),
        (topicId) => {
            const split = topicId.split('.');
            const head = split.shift();

            return {
                id: split.length ? head : '-',
                name: split.length ? split.join('.') : head,
                topic_id: topicId,
                topic: true
            };
        }
    );

    return [...transforms, ...topics];
});

let currentTab = ref('');

const setCurrentTab = (str) => {
    if (str === 'Source' || str === 'Destination') {
        return;
    }

    currentTab.value = str;

    router.push({
        query: { tab: currentTab.value, source: route.query.source, destination: route.query.destination, customMessage: route.query.customMessage }
    });
};

const updateCanSaveTree = (newValue) => {
    canSaveTree.value = newValue;
};

function onStatusClick(connector) {
    if (connector.connector_status === 'Broken' && connector.task_statuses) {
        const errors = [...new Set(Object.values(connector.task_statuses).map((task) => task.trace))];
        useErrorDialog({
            errors,
            width: 65
        });
    }
}

function selectedConnectorPath(id, type = 'source') {
    return type === 'source' ? { name: 'source-details', params: { id } } : { name: 'destination-details', params: { id } };
}

onMounted(async () => {
    // Set all loading states initially
    generalLoading.value = true;
    configurationSchemaLoading.value = true;
    transformsLoading.value = true;
    metricsLoading.value = true;
    tagsLoading.value = true;

    setCurrentTab(route.query.tab || 'Status');

    try {
        // Start all independent API calls immediately in parallel for maximum performance
        const coreDataPromise = getPipelineDetails(props.id, route?.query?.customMessage);
        const tagsPromise = tagsStore.fetch();
        const transformsPromise = transformsStore.fetchForConnectors();
        const connectorsPromise = sourcesStore.fetchConnectors();

        // Wait for core pipeline data and enable basic UI immediately
        const pipelineFetch = await coreDataPromise;
        pipeline.value = pipelineFetch;
        generalLoading.value = false; // Enable basic UI as soon as pipeline loads

        // Initialize tags if they're already loaded
        initializeSelectedTags();

        // Initialize default values if dependencies are already loaded
        initializeDefaultValues();

        // Start source loading using consolidated logic (no more if/else duplication)
        const sourceId = props.source || pipeline.value?.source?.id;
        const sourcePromise = sourceId ? sourcesStore.getById(sourceId) : Promise.resolve(null);

        // Process tags as soon as available (non-blocking UI rendering)
        tagsPromise.then(() => {
            tags.value = tagsStore.tagsPerTypeForEntityCRUD[TAG_TYPES.PIPELINE];
            initializeSelectedTags();
        });

        // Process transforms as soon as available (non-blocking UI rendering)
        transformsPromise.then(() => {
            if (transformsStore.allForConnectors && !_.isEmpty(transformsStore.allForConnectors)) {
                constructTransformTreeNodes();
            }
            transformsLoading.value = false; // Enable transforms section immediately
        });

        // Start metrics loading immediately (non-blocking UI rendering)
        const metricsPromise = pipeline.value?.id
            ? metricsStore.pipelines
                .fetch(pipeline.value.id)
                .then(() => {
                    metricsLoading.value = false; // Enable metrics section immediately
                })
                .catch((e) => {
                    console.log(e);
                    metricsLoading.value = false; // Enable section even on error
                })
            : Promise.resolve().then(() => {
                metricsLoading.value = false;
            });

        // Wait for source data and assign it
        const source = await sourcePromise;
        pipelineSource.value = source;

        // Start configuration loading and tree processing
        const configurationPromise = pipeline.value?.source?.connector
            ? sourcesStore.fetchConfiguration(pipeline.value.source.connector).then(() => {
                loadTreeViews();
                configurationSchemaLoading.value = false; // Enable schema section as soon as ready
            })
            : Promise.resolve().then(() => {
                configurationSchemaLoading.value = false;
            });

        // Set up default values as soon as essential data is available (non-blocking)
        Promise.all([tagsPromise, transformsPromise, connectorsPromise]).then(() => {
            initializeDefaultValues();
        });

        // Set up WebSocket listeners immediately (doesn't need to wait)
        addWebsocketListeners();

        // Wait for remaining heavy operations to complete (for cleanup)
        await Promise.all([configurationPromise, metricsPromise]);
    } catch (error) {
        console.error('Error loading pipeline details:', error);
        // Set loading states to false even on error to prevent infinite loading
        generalLoading.value = false;
        tagsLoading.value = false;
        transformsLoading.value = false;
        configurationSchemaLoading.value = false;
        metricsLoading.value = false;
    } finally {
        // Ensure all loading states are cleared
        generalLoading.value = false;
        configurationSchemaLoading.value = false;
        transformsLoading.value = false;
        metricsLoading.value = false;
        tagsLoading.value = false;
    }
});

const addWebsocketListeners = () => {
    WebSocketService.addListener({
        topic: 'metrics',
        entityType: 'pipelines',
        entityId: props.id,
        callback: metricsStore.onMetricsData,
        callbackId: 'metrics.onMetricsData'
    });
};

const removeWebsocketListeners = () => {
    WebSocketService.removeListener({
        topic: 'metrics',
        entityType: 'pipelines',
        entityId: props.id,
        callbackId: 'metrics.onMetricsData'
    });
};

onBeforeUnmount(() => {
    removeWebsocketListeners();
});

async function getPipelineDetails(id, customMsg) {
    var result = await pipelinesStore.getById(id, customMsg);

    if (customMsg && result) {
        router.push({ query: { ...route.query, customMessage: undefined } });
    }

    return result;
}

// Helper function to safely initialize selected tags (handles race conditions)
function initializeSelectedTags() {
    if (pipeline.value && tags.value) {
        // Handle case where pipeline.tags might be undefined or empty
        const pipelineTags = pipeline.value.tags || [];
        selectedTags.value = tags.value.filter((tag) => pipelineTags.includes(tag.id || tag._id));
        tagsLoading.value = false; // Enable tags section
    } else if (tags.value) {
        // Tags loaded but no pipeline yet
        tagsLoading.value = false;
    }
}

// Helper function to safely initialize default values (handles race conditions)
function initializeDefaultValues() {
    if (pipeline.value && tags.value) {
        defaultPipelineName.value = pipeline.value.name || '';
        defaultPipelineTags.value = tags.value?.filter((tag) => pipeline.value.tags?.includes(tag.id || tag._id));

        // Initialize periodic audit config from existing pipeline data
        periodicAuditConfig.value = pipeline.value.periodic_audit
            ? {
                enabled: true,
                topics: pipeline.value.periodic_audit.topics || [],
                timestampColumn: pipeline.value.periodic_audit.timestamp_column || '',
                intervalMinutes: pipeline.value.periodic_audit.interval_minutes || null
            }
            : { enabled: false, topics: [], timestampColumn: '', intervalMinutes: null };

        defaultAuditConfig.value = _.cloneDeep(periodicAuditConfig.value);
    }
}
function loadTreeViews() {
    if (pipelineSource.value) {
        try {
            pipelineSourceConfig.value = sourcesStore.configurations[pipeline.value.source.connector];

            if (pipelineSourceConfig.value) {
                constructTreeNodes();
            }

            const topics = formattedTopicsAndTransforms.value?.filter((x) => x.topic);

            setSelectedTopics(
                treeNodes.value,
                topics?.map((t) => ({ name: t.name, topic_id: t.topic_id })),
                ''
            );

            defaultSchemaTree.value = _.cloneDeep({ nodes: treeNodes.value, levels: treeLevels.value });
        } finally {
            transformsLoading.value = false;
        }
    }
}

function setSelectedTopics(nodes, topics, parentName) {
    let isSelected = false;

    for (let node of nodes) {
        let fullName = parentName ? parentName + '.' + node.name : node.name;

        // if the topic id is same as the fullName, mark it
        // if we have a parent and maybe a topic id that has also the parent included, and fullName (with parentName) is not equal, we check directly with the node name without parent
        // (since it might include already the parent name due to it being named like the id)

        if (
            topics.find(
                (x) => x.topic_id === fullName || x.name === fullName
                // || (parentName ? x.name === parentName || x.name === node.name || x.topic_id === node.name : false)
            )
        ) {
            node.isSelected = true;
            isSelected = true;
        }

        if (node.children) {
            if (setSelectedTopics(node.children, topics, fullName)) {
                node.isSelected = true;
                isSelected = true;
            }
        }
    }

    return isSelected;
}

const getFormattedValue = (metricKey, unit, dashOnRule = null) => {
    const metricValue = pipelineLatestMetrics.value?.inline?.[metricKey];

    const formatterParams = [metricValue, { unit }];

    if (dashOnRule || dashOnRule === false) {
        formatterParams.push(dashOnRule);
    }

    return formatter(...formatterParams);
};

const constructTreeNodes = () => {
    var configProps = pipelineSource.value.config;
    var sourceConnector = Object.values(sourcesStore.sourceConnectors)
        .flat()
        .find((x) => x.connector == pipelineSource.value.connector);

    const nodes = buildTreeNodesOutOfExistingConfig(configProps, pipelineSourceConfig.value, sourceConnector, false);

    treeLevels.value = nodes.treeLevels;
    treeNodes.value = nodes.treeNodes;

    return { nodes: treeNodes.value, levels: treeLevels.value };
};

const checkSchemaTreeValidity = (transformTreeValid = false) => {
    if (_.isEmpty(treeNodes.value) && _.isEmpty(treeLevels.value)) {
        return {
            valid: true,
            equal: true
        };
    }

    let valid = transformTreeNodes.value.some((x) => x.isSelected) ? transformTreeValid : false;

    let equal = false;
    const areNodesEqual = compareWithOmittedProperties(defaultSchemaTree.value.nodes, treeNodes.value, ['collapsed']);
    const areLevelsEqual = compareWithOmittedProperties(defaultSchemaTree.value.levels, treeLevels.value, ['collapsed']);

    if (treeNodes.value.some((x) => x.isSelected)) {
        valid = checkAllSelectedHaveSelectedChild(treeNodes.value);
    } else {
        // the schema tree is valid when nothing is selected only if transform tree has selected stuff (we can't have both empty)
        valid = checkPartialSelectedHaveSomeChild(transformTreeNodes.value);
    }

    equal = areNodesEqual && areLevelsEqual;

    return {
        valid,
        equal
    };
};

const checkTransformTreeValidity = (schemaTreeValid = false) => {
    if (_.isEmpty(transformTreeNodes.value) && _.isEmpty(transformTreeLevels.value)) {
        return {
            valid: true,
            equal: true
        };
    }

    let valid = treeNodes.value.some((x) => x.isSelected) ? schemaTreeValid : false;
    let equal = false;

    const areNodesEqual = compareWithOmittedProperties(defaultTransformsTree.value.nodes, transformTreeNodes.value, ['collapsed']);
    const areLevelsEqual = compareWithOmittedProperties(defaultTransformsTree.value.levels, transformTreeLevels.value, ['collapsed']);

    if (transformTreeNodes.value.some((x) => x.isSelected)) {
        valid = checkAllSelectedHaveSelectedChild(transformTreeNodes.value);
    } else {
        // the transform tree is valid when nothing is selected only if schema tree has selected stuff (we can't have both empty)
        valid = checkPartialSelectedHaveSomeChild(treeNodes.value);
    }

    equal = areNodesEqual && areLevelsEqual;

    return {
        valid,
        equal
    };
};

const nameChanged = computed(() => {
    return pipeline.value?.name && pipeline.value?.name !== defaultPipelineName.value;
});

const tagsChanged = computed(() => {
    return !_.isEqual(selectedTags.value, defaultPipelineTags.value);
});

const auditChanged = computed(() => {
    return !_.isEqual(periodicAuditConfig.value, defaultAuditConfig.value);
});

watch(
    treeNodes,
    () => {
        const transformTreeValid = checkTransformTreeValidity();
        const validity = checkSchemaTreeValidity(transformTreeValid.valid);

        canSaveTree.value = validity.valid;
        areTreesEqual.value = validity.equal && checkTransformTreeValidity(validity.valid).equal;
    },
    { deep: true }
);

watch(
    transformTreeNodes,
    () => {
        const schemaTreeValid = checkSchemaTreeValidity();
        const validity = checkTransformTreeValidity(schemaTreeValid.valid);

        canSaveTree.value = validity.valid;
        areTreesEqual.value = validity.equal && checkSchemaTreeValidity(validity.valid).equal;
    },
    { deep: true }
);

const checkAllSelectedHaveSelectedChild = (nodes) => {
    for (let obj of nodes) {
        if (obj.isSelected && obj.children && obj.children?.length > 0) {
            if (!obj.children?.some((child) => child.isSelected)) {
                return false;
            }
        }

        if (obj.isSelected && (!obj.children || !obj.children?.length)) {
            return true;
        }

        if (obj.children) {
            const childResult = checkAllSelectedHaveSelectedChild(obj.children);
            if (!childResult) {
                return false;
            }
        }
    }

    return true;
};

const checkPartialSelectedHaveSomeChild = (nodes) => {
    for (let obj of nodes) {
        if (obj.isSelected) {
            return true;
        }

        if (obj.children && obj.children?.length > 0) {
            if (obj.children?.some((child) => child.isSelected)) {
                return true;
            }
        }

        if (obj.children) {
            const childResult = checkPartialSelectedHaveSomeChild(obj.children);

            if (!childResult) {
                return false;
            }
        }
    }

    return false;
};

// Reactive list of selected source schema topics (leaf nodes)
const computedSourceTopics = computed(() => {
    const out = [];
    const collect = (nodes, prefix = '') => {
        nodes.forEach((node) => {
            const fullName = prefix ? `${prefix}.${node.name}` : node.name;
            if ((!node.children || node.children.length === 0) && node.isSelected) {
                out.push(fullName);
            }
            if (node.children) {
                collect(node.children, fullName);
            }
        });
    };
    collect(treeNodes.value, '');
    return out;
});

const constructTransformTreeNodes = () => {
    transformTreeLevels.value.push({ schemaLevel: 'schema', schemaObject: 'random' }, { schemaLevel: 'topic', schemaObject: 'random' });
    transformTreeNodes.value = [];
    const children = [];

    transformsStore.allForConnectors.forEach((transform) => {
        const isTransformIncluded = !!pipeline.value.transforms.find(
            (t) => t.name === transform.name && t.id === transform.id && t.topic_id === transform.topic_id
        );

        children.push({
            name: transform.topic,
            isSelected: isTransformIncluded,
            schemaLevel: 'topic',
            schemaObject: transform
        });
    });

    transformTreeNodes.value.push({
        name: 'transforms',
        isSelected: children?.filter((c) => c.isSelected).length === _.size(transformsStore.allForConnectors),
        collapsed: true,
        schemaLevel: 'schema',
        schemaObject: 'random',
        children
    });

    defaultTransformsTree.value = _.cloneDeep({ nodes: transformTreeNodes.value, levels: transformTreeLevels.value });
};

const getSelectedTransformTreeNodes = () => {
    if (transformTreeNodes.value.length === 0) {
        return [];
    }
    return transformTreeNodes.value[0].children.filter((topicNode) => topicNode.isSelected).map((topicNode) => topicNode.schemaObject);
};

onBeforeRouteLeave((to, from, next) => {
    if (generalLoading.value || actionLoading.value || configurationSchemaLoading.value || transformsLoading.value || metricsLoading.value) {
        return next();
    }

    if (hidePromptTemporary.value || (areTreesEqual.value && !nameChanged.value && !!pipeline.value.name && !tagsChanged.value)) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

const onDelete = () => {
    useConfirmBeforeAction(
        'delete',
        async () => {
            try {
                actionLoading.value = true;
                await pipelinesStore.delete(pipeline.value.id);
                actionLoading.value = false;

                pipelinesStore.fetch();
                router.push({ name: 'pipelines' });
            } finally {
                actionLoading.value = false;
            }
        },
        { entity: 'pipeline', name: pipeline.value.name },
        () => {
            actionLoading.value = false;
        }
    );
};

const handleTagSelection = () => {
    const existingExclusiveTag = selectedTags.value.filter((tag) => tag.type?.length && MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));

    if (existingExclusiveTag?.length > 1) {
        toast.warning(`Only one tag of the same ${MAX_ONE_TAG_PER_TYPE.join(', ')} type can be selected`);

        selectedTags.value = selectedTags.value.filter((tag) => tag.type?.length && !MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));
        selectedTags.value.push(existingExclusiveTag[0]);
        return;
    }
};

const onSavePipeline = () => {
    actionLoading.value = true;

    if (defaultPipelineTags.value?.length && !selectedTags.value?.length) {
        toast.error('Please select at least one tag. Having at least an environment-related tag (Production/Development) is mandatory.');

        actionLoading.value = false;
        return;
    }

    if (!selectedTags.value?.find((tag) => tag.type?.includes(TAG_TYPES.ENVIRONMENT))) {
        toast.error('Please select at least one environment-related tag (Production/Development).');

        actionLoading.value = false;
        return;
    }

    const auditPayload = periodicAuditConfig.value.enabled
        ? {
            topics: periodicAuditConfig.value.topics,
            timestamp_column: periodicAuditConfig.value.timestampColumn,
            interval_minutes: periodicAuditConfig.value.intervalMinutes
        }
        : null;

    pipelinesStore
        .edit(
            {
                name: pipeline.value.name,
                snapshot_new_tables: !!snapshotNewTables.value,
                destination: {
                    connector: pipeline.value.destination.connector,
                    name: pipeline.value.destination.name,
                    id: pipeline.value.destination.id
                },
                source: {
                    connector: pipeline.value.source.connector,
                    name: pipeline.value.source.name,
                    topics: computedSourceTopics.value,
                    id: pipeline.value.source.id
                },
                transforms: getSelectedTransformTreeNodes(),
                tags: selectedTags.value?.map((tag) => tag.id || tag._id),
                periodic_audit: auditPayload
            },
            props.id
        )
        .then(() => {
            hidePromptTemporary.value = true;
            pipelinesStore.fetch();
            router.push({ name: 'pipelines' });
        })
        .catch((err) => {
            formError.value = err;
        })
        .finally(() => {
            actionLoading.value = false;
            hidePromptTemporary.value = false;
        });
};

const onSnapshot = async () => {
    useConfirmBeforeAction(
        'snapshot',
        async () => {
            actionLoading.value = true;

            try {
                const snapshotBody = {
                    source_id: pipeline.value.source.id,
                    connector: pipeline.value.source.connector,
                    topic_names: pipeline.value.topics
                };

                await sourcesStore.snapshot(snapshotBody);
            } finally {
                actionLoading.value = false;
            }
        },
        { actionName: 'trigger a snapshot on all tables for', name: pipeline.value.name, typeInInput: 'snapshot' },
        () => {
            actionLoading.value = false;
        }
    );
};

const onCancelSnapshot = async () => {
    actionLoading.value = true;

    try {
        const snapshotBody = {
            source_id: pipeline.value.source.id,
            connector: pipeline.value.source.connector,
            topic_names: pipeline.value.topics
        };

        await sourcesStore.cancelSnapshot(snapshotBody);
    } finally {
        actionLoading.value = false;
    }
};

const canConnectorCancelSnapshot = () => {
    // Check if snapshot is disabled via global config
    const snapshotDisabled = getGlobalConfigValue(pipelineSourceConfig.value, 'incremental.snapshot.disabled');
    
    // If global config is not found (null), assume snapshots are enabled (false = not disabled)
    // If global config is found and true, then snapshots are disabled
    if (snapshotDisabled === true) return false;

    let canIt = true;

    // if (NO_SNAPSHOT_CONNECTORS.includes(pipeline.value?.source?.connector) || !pipeline.value?.topics?.length) return false;

    return canIt;
};

const canConnectorSnapshot = () => {
    // Check if snapshot is disabled via global config
    const snapshotDisabled = getGlobalConfigValue(pipelineSourceConfig.value, 'incremental.snapshot.disabled');
    
    // If global config is not found (null), assume snapshots are enabled (false = not disabled)
    // If global config is found and true, then snapshots are disabled
    if (snapshotDisabled === true) return false;

    // if (NO_SNAPSHOT_CONNECTORS.includes(pipeline.value?.source?.connector) || !pipeline.value?.topics?.length) return false;

    return true;
};

const handleTopicsConfirmModal = async (shouldSnapshot) => {
    if (_.isBoolean(shouldSnapshot)) {
        snapshotNewTables.value = shouldSnapshot;

        await onSavePipeline();
    }
};
</script>

<template>
    <div class="flex flex-col h-[calc(100vh-1rem)] overflow-hidden">
        <div class="page-header">
            <AppHeader :show-title="false">
                <template #titleRight>
                    <nav class="text-gray-700" aria-label="Breadcrumb">
                        <ol v-if="pipeline" class="list-none p-0 inline-flex font-semibold">
                            <li class="flex items-center">
                                <router-link to="/pipelines" class="text-green-500 hover:text-cyan-900">Pipelines</router-link>
                                <ChevronRightIcon class="w-4 h-4 mx-2 text-cyan-950" />
                                <h3 class="text-cyan-950 truncate" style="max-width: 25rem" :title="pipeline.name" :alt="pipeline.name">
                                    {{ pipeline.name }}
                                </h3>
                            </li>
                        </ol>
                    </nav>
                </template>
                <template #end>
                    <RowActionMenu
                        v-if="!generalLoading"
                        :show-stop-source="userHasAccess([$Permissions.WRITE_SOURCES], authStore.user) && showStopSource"
                        :show-stop-destination="userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user) && showStopDestination"
                        :show-resume-source="userHasAccess([$Permissions.WRITE_SOURCES], authStore.user) && showResumeSource"
                        :show-resume-destination="userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user) && showResumeDestination"
                        :show-delete="userHasAccess([$Permissions.DELETE_PIPELINES], authStore.user)"
                        :show-snapshot="canConnectorSnapshot()"
                        :show-cancel-snapshot="canConnectorCancelSnapshot()"
                        :loading="actionLoading"
                        :disabled="generalLoading || actionLoading"
                        @stopSource="onStopSource"
                        @resumeSource="onResumeSource"
                        @delete="onDelete"
                        @snapshot="onSnapshot"
                        @cancel-snapshot="onCancelSnapshot"
                        @stopDestination="onStopDestination"
                        @resumeDestination="onResumeDestination" />
                </template>
            </AppHeader>
        </div>
        <div class="flex-1 overflow-auto">
            <AppLoader :force-loading="generalLoading" default-height="75vh">
                <div v-if="pipeline" class="w-full mb-5">
                    <Tabs :tabs="tabs" :initial-active-tab="currentTab" :loading="tabLoading" @tab-change="setCurrentTab">
                        <template #Status>
                            <div class="mt-5">
                                <div class="w-full mt-3 mb-5">
                                    <h3 class="mb-3 font-medium">Tags</h3>
                                    <div
                                        v-if="tagsLoading || generalLoading"
                                        class="snippet mt-3 pb-3 ml-2 flex items-baseline"
                                        data-title=".dot-flashing">
                                        <div class="stage">
                                            <div class="dot-flashing"></div>
                                        </div>
                                    </div>
                                    <div v-else class="flex flex-wrap gap-2">
                                        <span
                                            v-for="tag in tags.filter((tag) => pipeline.tags.includes(tag.id || tag._id))"
                                            :key="tag.id || tag._id"
                                            class="inline-flex items-center justify-center rounded-full px-3 py-1 text-xs shadow-sm font-medium"
                                            :class="TAG_BADGE_COLOR_CLASSES_PER_TYPE[tag.type?.find((type) => type === 'pipelines') || tag.type[0]]"
                                            >{{ tag.name }}</span
                                        >
                                        <span v-if="!pipeline.tags?.length" class="text-gray-500">No tags found. Add them in Settings.</span>
                                    </div>
                                </div>
                                <div class="w-full mt-3">
                                    <h3 class="mb-3 font-medium">Stats</h3>

                                    <div class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                        <StatCard
                                            v-for="item in stats"
                                            :key="item.id"
                                            :icon-bg-class="item.colorClass"
                                            :info-icon="item.infoIcon"
                                            :errors="item.errors"
                                            :icon="item.icon"
                                            :loading="metricsLoading || configurationLoading"
                                            :title="item.name"
                                            :value="item.stat" />
                                    </div>
                                </div>
                                <div class="flex flex-col lg:flex-row w-full items-center gap-3 mt-5">
                                    <div class="w-full lg:w-1/2">
                                        <div class="font-semibold flex items-center gap-3 text-md mb-3 pr-3">Source</div>
                                        <div class="relative flex items-center rounded-lg shadow-md p-4 border gap-3 bg-white min-h-[5.5rem]">
                                            <div class="flex justify-between items-center w-full">
                                                <div class="flex items-center gap-2 ml-4 font-medium flex-col md:flex-row">
                                                    <router-link :to="selectedConnectorPath(pipeline.source.id, 'source')">
                                                        <div class="flex items-center gap-2 truncate">
                                                            <img :src="getConnectorIcon(pipeline.source.connector)" alt="" class="w-9 h-9" />
                                                            <span :title="pipeline.source.name" class="truncate" style="max-width: 25rem">{{
                                                                pipeline.source.name
                                                            }}</span>
                                                        </div>
                                                    </router-link>
                                                    <span
                                                        class="inline-flex items-center rounded-full px-3 py-1 text-xs bg-green-200 text-gray-800 shadow-sm font-medium"
                                                        title="Connectors"
                                                        >{{ getConnectorDisplayName(pipeline.source.connector) }}</span
                                                    >
                                                    <small
                                                        class="inline-flex items-center justify-center rounded-full font-medium py-1 px-2.5 text-gray-800 shadow-sm"
                                                        :class="[
                                                            'inline-flex items-center rounded-full font-medium py-1 px-2.5 text-gray-800 shadow-sm',
                                                            pipeline.source.connector_status === 'Broken' ? 'cursor-pointer' : ''
                                                        ]"
                                                        :style="`background: ${getStatusColor(pipeline.source.connector_status)}`"
                                                        @click="() => onStatusClick(pipeline.source)">
                                                        {{ pipeline.source.connector_status }}
                                                        <InformationCircleIcon
                                                            v-if="pipeline.source.connector_status === 'Broken'"
                                                            class="h-4 w-4 ml-1" />
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="w-full lg:w-1/2">
                                        <div class="font-semibold flex items-center gap-3 text-md mb-3 pr-3">Destination</div>
                                        <div class="relative flex items-center rounded-lg shadow-md p-4 border gap-3 bg-white min-h-[5.5rem]">
                                            <div class="flex justify-between items-center w-full">
                                                <div class="flex items-center gap-2 ml-4 font-medium flex-col md:flex-row">
                                                    <router-link :to="selectedConnectorPath(pipeline.destination.id, 'destination')">
                                                        <div class="flex items-center gap-1 truncate">
                                                            <img :src="getConnectorIcon(pipeline.destination.connector)" alt="" class="w-9 h-9" />
                                                            <span :title="pipeline.destination.name" class="truncate" style="max-width: 25rem">{{
                                                                pipeline.destination.name
                                                            }}</span>
                                                        </div>
                                                    </router-link>
                                                    <span
                                                        class="inline-flex items-center rounded-full px-3 py-1 text-xs bg-green-200 text-gray-800 shadow-sm font-medium"
                                                        title="Connectors"
                                                        >{{ getConnectorDisplayName(pipeline.destination.connector) }}</span
                                                    >
                                                    <small
                                                        :class="[
                                                            'inline-flex items-center rounded-full font-medium py-1 px-2.5 text-gray-800 shadow-sm',
                                                            pipeline.destination.connector_status === 'Broken' ? 'cursor-pointer' : ''
                                                        ]"
                                                        :style="`background: ${getStatusColor(pipeline.destination.connector_status)}`"
                                                        @click="() => onStatusClick(pipeline.destination)">
                                                        {{ pipeline.destination.connector_status }}
                                                        <InformationCircleIcon
                                                            v-if="pipeline.destination.connector_status === 'Broken'"
                                                            class="h-4 w-4 ml-1" />
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="formattedTopicsAndTransforms.length" class="w-full mt-5">
                                    <div class="font-semibold flex items-center gap-3 text-md mb-3 pr-3">Topics</div>
                                    <div class="relative w-full shadow-md overflow-auto">
                                        <table class="min-w-full divide-y divide-gray-300 border" aria-label="topics table">
                                            <thead class="bg-gray-50">
                                                <tr>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">ID</th>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                        NAME
                                                    </th>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                        TYPE
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody class="bg-white divide-y divide-gray-200">
                                                <tr
                                                    v-for="topicOrTransform in formattedTopicsAndTransforms"
                                                    :key="topicOrTransform.id"
                                                    class="transition duration-300 ease-in-out hover:bg-gray-50">
                                                    <td class="px-6 py-4 whitespace-nowrap">
                                                        {{ topicOrTransform.id }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap">
                                                        {{ topicOrTransform.name }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap">
                                                        <div class="gap-2 flex flex-col text-gray-600">
                                                            <div class="flex items-center gap-1 text-gray-600">
                                                                <QueueListIcon v-if="topicOrTransform.topic" class="h-5 w-5 text-gray-600" />
                                                                <AdjustmentsVerticalIcon v-else class="h-5 w-5 text-gray-600" />

                                                                {{ topicOrTransform.topic ? 'TOPIC' : 'TRANSFORM' }}
                                                            </div>

                                                            <small v-if="topicOrTransform.transform_name">
                                                                Transform: {{ topicOrTransform.transform_name }}</small
                                                            >
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template #Settings>
                            <div class="mt-5 w-full">
                                <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 p-5 rounded-lg shadow-md space-y-6 bg-white">
                                    <div class="space-y-8 border-cyan-950/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-cyan-950/10 sm:pb-0">
                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <label for="name" class="block text-md text-gray-600 font-semibold mb-2"> Name</label>
                                            <div class="flex items-center space-x-3">
                                                <input
                                                    id="name"
                                                    v-model="pipeline.name"
                                                    :disabled="generalLoading || actionLoading || transformsLoading || configurationSchemaLoading"
                                                    aria-label="input"
                                                    name="name"
                                                    type="text"
                                                    placeholder="Your pipeline name"
                                                    autocomplete="name"
                                                    required=""
                                                    class="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs sm:text-sm" />
                                            </div>
                                        </div>

                                        <div class="space-y-8 border-cyan-950/10 pb-6 sm:space-y-0 sm:divide-y sm:divide-cyan-950/10 sm:pb-0">
                                            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                                <label for="name" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5 flex items-center">
                                                    Tags
                                                    <a
                                                        href="https://docs.streamkap.com/docs/streamkap-support"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        class="ml-2 text-gray-500 hover:text-green-500 transition-colors duration-200">
                                                        <QuestionMarkCircleIcon class="w-5 h-5" />
                                                    </a>
                                                </label>
                                                <div class="flex items-center space-x-3 mb-8">
                                                    <multiselect
                                                        v-model="selectedTags"
                                                        tag-placeholder="Add this as new tag"
                                                        label="name"
                                                        track-by="id"
                                                        :options="tags"
                                                        :multiple="true"
                                                        :select-label="'Select'"
                                                        :deselect-label="'Remove'"
                                                        :taggable="false"
                                                        @select="handleTagSelection"
                                                        :placeholder="
                                                            tagsLoading ? 'Loading...' : tags?.length ? 'Search for tags' : 'No tags available'
                                                        "
                                                        :loading="tagsLoading"
                                                        :disabled="generalLoading || actionLoading || tagsLoading || !tags?.length"
                                                        :class="validationClass"></multiselect>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- <div class="space-y-8 border-cyan-950/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-cyan-950/10 sm:pb-0">
                                            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                                <label for="name" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5">
                                                    Snapshot new topics
                                                </label>
                                                <div class="flex items-center space-x-3">
                                                    <Switch
                                                        v-model="snapshotNewTables"
                                                        :class="[
                                                            snapshotNewTables ? 'bg-green-400' : 'bg-neutral-300',
                                                            'relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
                                                            transformsLoading || configurationSchemaLoading ? 'disabled-toggle' : ''
                                                        ]"
                                                        :true-value="true"
                                                        :false-value="false"
                                                        :disabled="transformsLoading || configurationSchemaLoading"
                                                        aria-label="input"
                                                        aria-describedby="toggle">
                                                        <span
                                                            aria-hidden="true"
                                                            :class="[
                                                                snapshotNewTables ? 'translate-x-5' : 'translate-x-0',
                                                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out'
                                                            ]" />
                                                    </Switch>
                                                    <HalfCircleSpinner :loading="transformsLoading || configurationSchemaLoading" />
                                                </div>
                                            </div>
                                        </div> -->

                                        <PeriodicAuditConfig
                                            v-model="periodicAuditConfig"
                                            :source-topics="computedSourceTopics"
                                            :tree-nodes="treeNodes"
                                            :tree-levels="treeLevels"
                                            :loading="transformsLoading || configurationSchemaLoading"
                                            v-if="isAuditAllowed && pipeline" />

                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <label class="text-sm font-semibold text-cyan-950 sm:pt-1.5"> Source Schema </label>
                                            <div class="sm:col-span-2 mt-2 sm:mt-0">
                                                <div class="flex w-full rounded-lg border border-dashed border-cyan-950/25 p-6">
                                                    <AppLoader :force-loading="configurationSchemaLoading" default-height="15vh" class="w-full">
                                                        <TreeView
                                                            v-if="treeNodes && treeNodes.length > 0 && treeLevels && treeLevels[0] != undefined"
                                                            :tree-nodes="treeNodes"
                                                            :allow-search="true"
                                                            class="w-full"
                                                            :tree-levels="treeLevels"
                                                            :disable-create-and-edit="true"
                                                            :custom-width="'100%'"
                                                            :edit-mode="true"
                                                            @can-save-duplicate="updateCanSaveTree" />

                                                        <div v-else class="w-full">
                                                            <div class="text-center w-full">
                                                                <div class="flex flex-col w-full max-w-sm px-4 mx-auto items-center justify-center">
                                                                    <div class="p-3 mx-auto text-cyan-950 bg-green-100 rounded-full">
                                                                        <MagnifyingGlassIcon class="h-7 w-7" />
                                                                    </div>
                                                                    <p class="mt-2 text-cyan-950 text-xs">No schema available</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </AppLoader>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <label class="text-sm font-semibold text-cyan-950 sm:pt-1.5"> Transforms </label>
                                            <div class="sm:col-span-2 mt-2 sm:mt-0">
                                                <div class="flex w-full rounded-lg border border-dashed border-cyan-950/25 p-6">
                                                    <AppLoader :force-loading="transformsLoading" default-height="15vh" class="w-full">
                                                        <TreeView
                                                            v-if="transformTreeNodes && transformTreeLevels && transformTreeLevels[0] != undefined"
                                                            class="w-full"
                                                            :tree-nodes="transformTreeNodes"
                                                            :tree-levels="transformTreeLevels"
                                                            :custom-width="'100%'"
                                                            :allow-search="true"
                                                            :disable-create-and-edit="true"
                                                            :edit-mode="true"
                                                            @can-save-duplicate="updateCanSaveTree()" />

                                                        <div v-else class="w-full">
                                                            <div class="text-center w-full">
                                                                <div class="flex flex-col w-full max-w-sm px-4 mx-auto items-center justify-center">
                                                                    <div class="p-3 mx-auto text-cyan-950 bg-green-100 rounded-full">
                                                                        <MagnifyingGlassIcon class="h-7 w-7" />
                                                                    </div>
                                                                    <p class="mt-2 text-cyan-950 text-xs">No transforms found</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </AppLoader>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'pipeline-details'" class="mt-3" />
                                </div>
                            </div>
                        </template>
                    </Tabs>
                </div>
            </AppLoader>
        </div>

        <div class="mt-auto">
            <AppFooter v-if="pipeline" v-can-access.permissions="[$Permissions.WRITE_PIPELINES]">
                <template #right>
                    <RowActionMenu
                        v-if="!generalLoading"
                        :show-save="true"
                        :show-discard="false"
                        :disable-save="
                            !selectedTags?.length ||
                            !pipeline?.name ||
                            (nameChanged ? !canSaveTree : false) ||
                            (tagsChanged ? !canSaveTree : false) ||
                            !canSaveTree ||
                            (canSaveTree && areTreesEqual && !nameChanged && !tagsChanged && !auditChanged) ||
                            (periodicAuditConfig.enabled && !auditValid) ||
                            generalLoading ||
                            configurationSchemaLoading ||
                            transformsLoading ||
                            tagsLoading
                        "
                        :loading="actionLoading"
                        :disabled="generalLoading || actionLoading"
                        @save="canConnectorSnapshot() ? isTopicsConfirmModalOpen = true : onSavePipeline()" />
                </template>
            </AppFooter>
        </div>
    </div>

    <YesNoConfirmModal
        v-if="isTopicsConfirmModalOpen && canConnectorSnapshot()"
        :is-open="isTopicsConfirmModalOpen"
        title="Snapshot New Topics?"
        @update:isOpen="isTopicsConfirmModalOpen = $event"
        @confirm="handleTopicsConfirmModal">
        <p class="text-sm text-gray-700">The save will proceed now. Would you also like to <b>snapshot the new topics</b>?</p>
    </YesNoConfirmModal>
</template>

<style></style>
